import React from 'react';
import { Box, Stack } from '@mui/material';

import {
  formatForce,
  formatMoment,
  formatRadiansAsDegrees,
  MaterialPropertyValuePair,
  PropertySelectField,
} from '@paragon/client-tools';
import {
  AnalogJoint,
  AnalogJointResult,
  BOUNDARY_TYPES,
  BoundaryType,
  formatBoundaryType,
  PlatingForceResult,
} from '@paragon/contract-library';
import { PropertiesPanel, PropertiesSubpanel } from '@paragon/layout-models';
import { isLoggedInUserEngineer } from '@paragon/login-tools/source/scripts/selectors/users';

import { changeJointBoundaryType } from '../../../../store/actions/editAnalogModelActions';
import { getObjectCheckFailureSummaryString } from '../../../../store/selectors/analysisSetsSelectors';
import {
  getActiveBearingReactions,
  getActivePlatingForceResults,
} from '../../../../store/selectors/componentForcesSelectors';
import { getActiveAnalysisSet } from '../../../../store/selectors/getActiveAnalysisSetSelector';
import { getDistanceFormatterSelector } from '../../../../store/selectors/getDistanceFormatterSelector';
import { getMaxJsiForSingleSelectedItem } from '../../../../store/selectors/getMaxCsiOrJsiForSelectedItemSelector';
import { getSingleSelectedItemEntryOrNull } from '../../../../store/selectors/getSelectedItemSelector';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';

interface PlatingForcesProps {
  readonly joint: AnalogJoint;
  readonly platingForces: readonly PlatingForceResult[] | null;
}

function PlatingForces({ joint, platingForces }: PlatingForcesProps) {
  if (!platingForces) return null;

  const platingForceResult =
    platingForces && platingForces.length !== 0
      ? platingForces
          .filter((plateRes) => joint.guid === plateRes.analogJointGuid)
          .map((force, index) => (
            <PropertiesSubpanel title={`Force-${index}`} key={force.guid}>
              <Stack spacing={1}>
                <MaterialPropertyValuePair sx={{ m: 1, flex: 1 }} property="Member Name" value={force.memberName} />
                <MaterialPropertyValuePair
                  sx={{ m: 1, flex: 1 }}
                  property="Boundary Type"
                  value={force.platingForceType}
                />
                <MaterialPropertyValuePair
                  sx={{ m: 1, flex: 1 }}
                  property="X Force"
                  value={formatForce(force.xForce)}
                />
                <MaterialPropertyValuePair
                  sx={{ m: 1, flex: 1 }}
                  property="Y Force"
                  value={formatForce(force.yForce)}
                />
                <MaterialPropertyValuePair
                  sx={{ m: 1, flex: 1 }}
                  property="Moment"
                  value={formatMoment(force.moment)}
                />
              </Stack>
            </PropertiesSubpanel>
          ))
      : null;

  return <PropertiesSubpanel title="Plating Forces">{platingForceResult}</PropertiesSubpanel>;
}

interface BearingReactionsProps {
  readonly joint: AnalogJoint;
  readonly bearingForces: readonly AnalogJointResult[] | null;
  readonly distanceFormatter: (lengthInInches: number) => string;
}

function BearingReactions({ joint, bearingForces, distanceFormatter }: BearingReactionsProps) {
  let result = null;
  if (bearingForces) {
    // only one bearing reaction per joint??
    const bearingForceResult = bearingForces.filter((bearingRes) => joint.guid === bearingRes.analogJointGuid);

    if (bearingForceResult[0]) {
      result = (
        <PropertiesSubpanel title="Bearing Reactions">
          <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property="X Deflection"
              value={distanceFormatter(bearingForceResult[0].xDeflection)}
            />
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property="Y Deflection"
              value={distanceFormatter(bearingForceResult[0].yDeflection)}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property="Angular Deflection"
              value={formatRadiansAsDegrees(bearingForceResult[0].angularDeflection)}
            />
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property="X Force"
              value={formatForce(bearingForceResult[0].xForce)}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property="Y Force"
              value={formatForce(bearingForceResult[0].yForce)}
            />
            <MaterialPropertyValuePair
              sx={{ m: 1, flex: 1 }}
              property="Moment"
              value={formatMoment(bearingForceResult[0].moment)}
            />
          </Box>
        </PropertiesSubpanel>
      );
    }
  }

  return result;
}

export function JointProperties() {
  const joint = useAppSelector((state) => getSingleSelectedItemEntryOrNull(state)!.item as AnalogJoint);
  const platingForces = useAppSelector(getActivePlatingForceResults);
  const bearingForces = useAppSelector(getActiveBearingReactions);
  const maxJsi = useAppSelector(getMaxJsiForSingleSelectedItem);
  const isViewOnly = useAppSelector((state) => propertiesShouldBeViewOnly(state) || !isLoggedInUserEngineer(state));
  const componentDesignGuid = useAppSelector((state) => state.selections.activeComponentDesignGuid);
  const analysisSet = useAppSelector(getActiveAnalysisSet);
  const checkFailureSummaryString = getObjectCheckFailureSummaryString(joint.guid, analysisSet);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);

  const dispatch = useAppDispatch();

  return (
    <PropertiesPanel title="Joint Properties">
      <Stack spacing={2}>
        <MaterialPropertyValuePair sx={{ m: 1, flex: 1 }} property="Joint Name" value={joint.name} />

        {maxJsi ? <MaterialPropertyValuePair sx={{ m: 1, flex: 1 }} property="Max Jsi" value={maxJsi} /> : null}

        <PropertySelectField<BoundaryType>
          sx={{ m: 1, flex: 1 }}
          disabled={isViewOnly}
          value={joint.boundaryType}
          property="Boundary"
          getName={formatBoundaryType}
          submit={(value) => {
            dispatch(changeJointBoundaryType(componentDesignGuid!, joint.guid, value));
          }}
          options={BOUNDARY_TYPES}
        />

        {checkFailureSummaryString !== '' && (
          <MaterialPropertyValuePair
            sx={{ m: 1, flex: 1 }}
            property={`Check Failure Summary`}
            value={checkFailureSummaryString}
          />
        )}
      </Stack>

      {platingForces || bearingForces ? (
        <PropertiesSubpanel title="Force Analysis Data">
          <BearingReactions joint={joint} bearingForces={bearingForces} distanceFormatter={distanceFormatter} />
          <PlatingForces joint={joint} platingForces={platingForces} />
        </PropertiesSubpanel>
      ) : null}
    </PropertiesPanel>
  );
}
